import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Video, Phone as PhoneIcon, MapPin, Mail, Phone, User as UserIcon, Plus, LogOut, ExternalLink, Edit3, Save, X, FolderLock, ClipboardList, Bell, UserCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate, fmtTime, fmtDay, StatusBadge } from '../lib/format';
import { AvailabilityPill, Avatar } from '../components/SpecialistCard';
import MemberTaxFilingProgressCard from '../components/MemberTaxFilingProgressCard';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';

const LOCATION_ICON = { online: Video, phone: PhoneIcon, in_person: MapPin };
const API_HOST = process.env.REACT_APP_BACKEND_URL;

export default function AccountPage() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [mySpecialist, setMySpecialist] = useState(null);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    api.myAppointments().then((d) => setAppts(d)).finally(() => setLoading(false));
    api.myAssignedSpecialist().then((d) => setMySpecialist(d?.specialist || null)).catch(() => {});
    api.myOffers('pending').then(setOffers).catch(() => setOffers([]));
  }, []);

  const upcoming = appts.filter((a) => new Date(a.start) >= new Date() && a.status !== 'cancelled' && a.status !== 'completed');
  const past = appts.filter((a) => !(new Date(a.start) >= new Date()) || a.status === 'cancelled' || a.status === 'completed');

  const saveProfile = async () => {
    try {
      const u = await api.updateProfile({ name: form.name, phone: form.phone });
      localStorage.setItem('wyse_user', JSON.stringify(u));
      toast.success(t('acct.profileUpdated'));
      setEditing(false);
    } catch { toast.error(t('acct.profileErr')); }
  };

  const onLogout = () => { logout(); nav('/'); };

  return (
    <>
      <Header />
      <section className="py-12 bg-[#fbf7ee] border-b border-neutral-100">
        <div className="max-w-[1200px] mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#f5b300] text-[#14110f] flex items-center justify-center font-extrabold text-[22px]">
              {(user?.name || 'U').trim().slice(0, 1).toUpperCase()}
            </div>
            <div>
              <p className="section-eyebrow mb-1">{t('acct.eyebrow')}</p>
              <h1 className="text-[28px] md:text-[34px] font-extrabold text-[#14110f] leading-tight">{t('acct.hi')}, {user?.name?.split(' ')[0] || ''}</h1>
              <p className="text-[13.5px] text-neutral-600">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <Link to="/account/documents" data-testid="acct-open-docs" className="inline-flex items-center gap-2 bg-[#14110f] hover:bg-[#291d12] text-white font-semibold px-5 py-3 rounded-md shadow-[0_10px_24px_-10px_rgba(20,17,15,0.6)] transition"><FolderLock className="w-4 h-4" /> {t('acct.docVault')}</Link>
            <Link to="/account/tax-file" data-testid="acct-open-tax-file" className="inline-flex items-center gap-2 border border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 text-[#14110f] font-semibold px-4 py-3 rounded-md transition"><ClipboardList className="w-4 h-4" /> {t('acct.taxFile')}</Link>
            <Link to="/book" className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-3 rounded-md shadow-[0_10px_24px_-10px_rgba(245,179,0,0.7)] transition"><Plus className="w-4 h-4" /> {t('acct.bookNew')}</Link>
            <button onClick={onLogout} className="inline-flex items-center gap-2 border border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 text-[#14110f] font-semibold px-4 py-3 rounded-md transition"><LogOut className="w-4 h-4" /> {t('acct.signOut')}</button>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 space-y-6">

          {offers.length > 0 && (
            <Link to="/account/specialist" data-testid="offers-banner"
                  className="block rounded-xl border-2 border-[#f5b300] bg-gradient-to-br from-[#fff8e3] to-white p-5 hover:shadow-lg transition">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#f5b300]/20 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-[#c89200]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[#14110f] text-[15px]">
                    {offers.length} {offers.length === 1 ? t('acct.taxSpecialist') : t('acct.taxSpecialistPlural')} {offers.length === 1 ? t('acct.offersWants') : t('acct.offersWantPlural')}
                  </p>
                  <p className="text-[12.5px] text-neutral-600 mt-0.5">
                    {offers.map((o) => o.specialist_name).filter(Boolean).slice(0, 3).join(', ') || t('acct.offersSpecFallback')} {t('acct.offersDesc')}
                  </p>
                </div>
              </div>
            </Link>
          )}

          {/* Tax Filing Progress Tracker — full-width hero card */}
          <MemberTaxFilingProgressCard compact />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7">
          <div className="lg:col-span-2 space-y-7">
            <div className="rounded-xl border border-neutral-100 bg-white p-6 shadow-[0_15px_40px_-25px_rgba(20,17,15,0.18)]">
              <h3 className="text-[18px] font-bold text-[#14110f] mb-5">{t('acct.upcoming')}</h3>
              {loading ? <p className="text-neutral-400 text-[13.5px]">{t('acct.loading')}</p>
                : upcoming.length === 0 ? (
                  <div className="py-10 text-center">
                    <Calendar className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
                    <p className="text-[14px] text-neutral-500">{t('acct.noUpcoming')}</p>
                    <Link to="/book" className="mt-4 inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-2.5 rounded-md text-[13px]">{t('acct.bookFirst')}</Link>
                  </div>
                ) : (
                  <ul className="divide-y divide-neutral-100">
                    {upcoming.map((a) => <ApptRow key={a.id} a={a} t={t} />)}
                  </ul>
                )}
            </div>

            <div className="rounded-xl border border-neutral-100 bg-white p-6 shadow-[0_15px_40px_-25px_rgba(20,17,15,0.18)]">
              <h3 className="text-[18px] font-bold text-[#14110f] mb-5">{t('acct.history')}</h3>
              {past.length === 0 ? <p className="text-[13.5px] text-neutral-400">{t('acct.noPast')}</p>
                : <ul className="divide-y divide-neutral-100">{past.map((a) => <ApptRow key={a.id} a={a} t={t} />)}</ul>}
            </div>
          </div>

          <aside className="space-y-7">
            {/* My Tax Specialist */}
            <Link to="/account/specialist" data-testid="my-specialist-widget"
                  className="block rounded-xl border border-neutral-100 bg-white p-5 hover:border-[#f5b300]/40 hover:shadow-[0_15px_40px_-25px_rgba(20,17,15,0.18)] transition group">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">{t('acct.mySpecialist')}</p>
                <ExternalLink className="w-3.5 h-3.5 text-neutral-300 group-hover:text-[#c89200]" />
              </div>
              {mySpecialist ? (
                <div className="flex items-center gap-3">
                  <Avatar src={mySpecialist.profile_picture_url} name={mySpecialist.name} size={48} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-bold text-[#14110f] truncate">{mySpecialist.name}</p>
                    <p className="text-[12px] text-neutral-500 truncate">{mySpecialist.title}</p>
                    <div className="mt-1"><AvailabilityPill status={mySpecialist.availability} /></div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-neutral-100 text-neutral-400 flex items-center justify-center">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-[#14110f]">{t('acct.noSpec')}</p>
                    <p className="text-[12px] text-neutral-500">{t('acct.noSpecDesc')}</p>
                  </div>
                </div>
              )}
            </Link>

            <div className="rounded-xl border border-neutral-100 bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[17px] font-bold text-[#14110f]">{t('acct.profile')}</h3>
                {!editing ? <button onClick={() => setEditing(true)} className="text-[12px] text-[#c89200] font-semibold hover:underline flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> {t('acct.edit')}</button>
                  : <button onClick={() => setEditing(false)} className="text-[12px] text-neutral-500 hover:text-neutral-700 flex items-center gap-1"><X className="w-3.5 h-3.5" /> {t('acct.cancel')}</button>}
              </div>
              {!editing ? (
                <div className="space-y-3 text-[13.5px]">
                  <p className="flex items-center gap-2.5 text-[#14110f]"><UserIcon className="w-4 h-4 text-[#c89200]" /> {user?.name}</p>
                  <p className="flex items-center gap-2.5 text-neutral-700"><Mail className="w-4 h-4 text-[#c89200]" /> {user?.email}</p>
                  {user?.phone && <p className="flex items-center gap-2.5 text-neutral-700"><Phone className="w-4 h-4 text-[#c89200]" /> {user.phone}</p>}
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400 mt-3">{t('acct.role')}: <span className="text-[#c89200] font-semibold">{user?.role}</span></p>
                </div>
              ) : (
                <div className="space-y-3">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('acct.fullName')} className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t('acct.phonePh')} className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
                  <button onClick={saveProfile} className="w-full inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2.5 rounded-md text-[13px]"><Save className="w-4 h-4" /> {t('acct.save')}</button>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-neutral-100 bg-[#faf6ec] p-6">
              <h3 className="text-[15px] font-bold text-[#14110f]">{t('acct.helpTitle')}</h3>
              <p className="mt-1.5 text-[13px] text-neutral-600">{t('acct.helpBody')} <a href="mailto:info@wysetribe.ca" className="text-[#c89200] underline">info@wysetribe.ca</a>.</p>
            </div>

            <Link to="/account/notifications" data-testid="acct-open-notifications"
                  className="block rounded-xl border border-neutral-100 bg-white p-5 hover:border-[#f5b300]/40 hover:shadow-[0_15px_40px_-25px_rgba(20,17,15,0.18)] transition group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[#fff8e3] text-[#c89200] flex items-center justify-center">
                  <Bell className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14.5px] font-bold text-[#14110f]">{t('acct.emailNotifsTitle')}</p>
                  <p className="text-[12.5px] text-neutral-500 mt-0.5">{t('acct.emailNotifsBody')}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-neutral-300 group-hover:text-[#c89200]" />
              </div>
            </Link>
          </aside>
        </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

function ApptRow({ a, t }) {
  const LocIcon = LOCATION_ICON[a.location_type] || Video;
  return (
    <li className="py-4 flex flex-wrap items-center gap-4">
      <div className="w-14 text-center rounded-lg bg-[#faf6ec] py-2">
        <p className="text-[10.5px] uppercase tracking-wider text-neutral-500">{new Date(a.start).toLocaleDateString('en-CA', { month: 'short' })}</p>
        <p className="text-[20px] font-extrabold text-[#14110f] leading-none">{new Date(a.start).getDate()}</p>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[14.5px] font-bold text-[#14110f]">{a.appointment_type_name}</p>
        <p className="text-[12.5px] text-neutral-500 flex items-center gap-3 flex-wrap mt-1">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fmtTime(a.start)} · {a.duration_minutes} min</span>
          <span className="flex items-center gap-1"><LocIcon className="w-3.5 h-3.5" /> {a.location_type.replace('_', ' ')}</span>
          {a.advisor?.name && <span>{t('acct.with')} {a.advisor.name}</span>}
        </p>
      </div>
      <StatusBadge status={a.status} />
      <Link to={`/book/manage/${a.token}`} className="text-[12px] text-[#c89200] font-semibold uppercase tracking-wider hover:underline flex items-center gap-1">{t('acct.manage')} <ExternalLink className="w-3.5 h-3.5" /></Link>
    </li>
  );
}
